// Enhanced trade list — TradingView-style "List of trades" tab. Each trade is a pair of
// rows (entry + exit) showing direction, date+time, signal label, price, size, net P&L,
// favourable excursion (MFE), adverse excursion (MAE), and a running cumulative P&L.
//
// Sortable by Trade # (default) — clicking the # header reverses the order.

import { useMemo, useState } from "react";
import type { BacktestResponseDto, ScanResultDto } from "../../api/types";
import { exitMeta } from "./primary";

interface Props {
  result: BacktestResponseDto;
}

interface Trade {
  num: number;
  direction: "long" | "short";
  entryTime: string;
  exitTime: string;
  entryPrice: number;
  exitPrice: number;
  exitSignal: string;       // "Stop loss" / "Take profit" / "Reverse (SAR)" / "Bars exit" / …
  exitColor: string;        // colour-codes the exit reason (sl=red, tp=green, reverse=blue…)
  ret: number;
  mae: number;
  mfe: number;
}

function collectTrades(result: BacktestResponseDto): Trade[] {
  // Phase 12: the netting account is the primary, chronologically-ordered trade stream and
  // is the ONLY block carrying per-trade exit types (SL / TP / trailing / reverse / …).
  if (result.strategy) {
    return result.strategy.tradeLog.map((t, i) => {
      const meta = exitMeta(t.exitType);
      return {
        num: i + 1,
        direction: t.direction === "short" ? "short" : "long",
        entryTime: t.entryTime,
        exitTime: t.exitTime,
        entryPrice: t.entryPrice,
        exitPrice: t.exitPrice,
        exitSignal: meta.label,
        exitColor: meta.color,
        ret: t.return,
        mae: t.mae,
        mfe: t.mfe,
      };
    });
  }
  // Prefer merged for the "All" view since merged is chronologically ordered. Fall back to
  // long+short interleaved by entry time.
  if (result.merged) {
    return result.merged.trades.map((t, i) => ({
      num: i + 1,
      direction: t.direction === "short" ? "short" : "long",
      entryTime: t.entryTime,
      exitTime: t.exitTime,
      entryPrice: t.entryPrice,
      exitPrice: t.exitPrice,
      exitSignal: exitMeta(t.exitType).label,
      exitColor: exitMeta(t.exitType).color,
      ret: t.return,
      mae: 0,  // merge trades don't track MAE/MFE
      mfe: 0,
    }));
  }
  const out: Trade[] = [];
  const addScan = (s: ScanResultDto | null, dir: "long" | "short") => {
    if (!s) return;
    for (let i = 0; i < s.trades; i++) {
      out.push({
        num: 0, // assigned after sort by entry time
        direction: dir,
        entryTime: s.entryTimes[i],
        exitTime: s.exitTimes[i],
        entryPrice: s.entryPrices[i],
        exitPrice: s.exitPrices[i],
        exitSignal: "Bars exit",
        exitColor: exitMeta("bars").color,
        ret: s.returns[i],
        mae: s.maeList[i],
        mfe: s.mfeList[i],
      });
    }
  };
  addScan(result.long, "long");
  addScan(result.short, "short");
  // Sort by entry time so the merged "All" view is chronological.
  out.sort((a, b) => Date.parse(a.entryTime) - Date.parse(b.entryTime));
  out.forEach((t, i) => { t.num = i + 1; });
  return out;
}

export function TradeList({ result }: Props) {
  const [sortAsc, setSortAsc] = useState(true);
  const trades = useMemo(() => collectTrades(result), [result]);

  const sorted = useMemo(() => {
    const copy = [...trades];
    if (!sortAsc) copy.reverse();
    return copy;
  }, [trades, sortAsc]);

  // Cumulative P&L, computed in chronological (ascending) order — even when displayed
  // descending, the cumulative shown on each row is its true ordinal cumulative.
  const cumulative = useMemo(() => {
    const m = new Map<number, number>();
    let acc = 0;
    for (const t of trades) {
      acc += t.ret;
      m.set(t.num, acc);
    }
    return m;
  }, [trades]);

  if (trades.length === 0) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No trades to list.
      </div>
    );
  }

  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>List of trades</div>
        <div style={{ fontSize: 11, color: "#8b949e" }}>{trades.length} trades</div>
      </div>
      <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #30363d", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "monospace" }}>
          <thead>
            <tr>
              <th onClick={() => setSortAsc((a) => !a)} style={{ ...thStyle, cursor: "pointer" }}>
                Trade #{sortAsc ? " ▲" : " ▼"}
              </th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Date and time</th>
              <th style={thStyle}>Signal</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Price</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Net P&L</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Favorable excursion</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Adverse excursion</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Cumulative P&L</th>
            </tr>
          </thead>
          <tbody>
            {sorted.flatMap((t) => [
              <tr key={`${t.num}-e`} style={{ borderTop: "1px solid #21262d" }}>
                <td rowSpan={2} style={{ ...tdStyle, color: "#58a6ff", fontWeight: 600, verticalAlign: "middle" }}>
                  {t.num} <span style={{ color: t.direction === "long" ? "#3fb950" : "#f85149", fontWeight: 600 }}>{t.direction.toUpperCase()}</span>
                </td>
                <td style={tdStyle}>Entry</td>
                <td style={tdStyle}>{fmtDate(t.entryTime)}</td>
                <td style={{ ...tdStyle, color: t.direction === "long" ? "#3fb950" : "#f85149" }}>{t.direction === "long" ? "Long" : "Short"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{fmtNum(t.entryPrice)}</td>
                <td rowSpan={2} style={{ ...tdStyle, textAlign: "right", color: t.ret >= 0 ? "#3fb950" : "#f85149", verticalAlign: "middle", fontWeight: 600 }}>
                  {fmtPct(t.ret)}
                </td>
                <td rowSpan={2} style={{ ...tdStyle, textAlign: "right", color: "#3fb950", verticalAlign: "middle" }}>
                  {t.mfe > 0 ? fmtPct(t.mfe) : "—"}
                </td>
                <td rowSpan={2} style={{ ...tdStyle, textAlign: "right", color: "#f85149", verticalAlign: "middle" }}>
                  {t.mae < 0 ? fmtPct(t.mae) : "—"}
                </td>
                <td rowSpan={2} style={{
                  ...tdStyle,
                  textAlign: "right",
                  verticalAlign: "middle",
                  color: (cumulative.get(t.num) ?? 0) >= 0 ? "#3fb950" : "#f85149",
                }}>
                  {fmtPct(cumulative.get(t.num) ?? 0)}
                </td>
              </tr>,
              <tr key={`${t.num}-x`}>
                <td style={tdStyle}>Exit</td>
                <td style={tdStyle}>{fmtDate(t.exitTime)}</td>
                <td style={{ ...tdStyle, color: t.exitColor, fontWeight: 600 }}>{t.exitSignal}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{fmtNum(t.exitPrice)}</td>
              </tr>,
            ])}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "6px 8px",
  textAlign: "left",
  fontSize: 11,
  color: "#8b949e",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  background: "#161b22",
  position: "sticky",
  top: 0,
};
const tdStyle: React.CSSProperties = { padding: "4px 8px", color: "#c9d1d9" };

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Match TV: "May 11, 2026, 12:03"
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n.toFixed(2);
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}
